import React from 'react';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { TranslateFunction } from 'react-localize-redux';

import { Add as AddIcon, Remove as RemoveIcon } from '../../icons';
import { IconButton, Typography } from '..';

export interface QuantityControlProps {
  addItemButtonColor?: 'primary' | 'secondary';
  className?: string;
  isRemoveItemDisabled?: boolean;
  itemName: string;
  onAddItem: (any) => void;
  onRemoveItem?: (any) => void;
  quantity?: number;
  translate: TranslateFunction;
}

const useStyles = makeStyles((theme: Theme) => ({
  quantityControl: {
    alignItems: 'center',
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'flex-end',

    '& .quantity': {
      '&--hidden': {
        display: 'none',
      },
      '&--visible': {
        display: 'block',
      },
    },
    '& .primaryColor': {
      backgroundColor: theme.palette.primary.main,

      '& .fd-icon svg': {
        color: theme.palette.common.white,
      },
      '&:hover': {
        backgroundColor: theme.palette.primary.light,
      },
    },
  },
  quantityValue: {
    minWidth: '24px',
    padding: theme.spacing(0, 0.25),

    '&.fd-text': {
      textAlign: 'center',
    },
  },
}));

export const QuantityControl = (props: QuantityControlProps): JSX.Element => {
  const {
    addItemButtonColor = 'primary',
    className = '',
    isRemoveItemDisabled = false,
    itemName,
    onAddItem,
    onRemoveItem,
    quantity = 1,
    translate,
  } = props;
  const classes = useStyles();

  const quantityControlStyles = clsx(
    'fd-quantity-control',
    'removeItemButton', // leave class in case COPS use it
    classes.quantityControl,
    className,
    quantity > 0 && 'hasQuantity',
  );
  const quantityControlAddStyles = clsx(
    'fd-quantity-control__add-button',
    'addItemButton', // leave class in case COPS use it
    addItemButtonColor === 'primary' && 'primaryColor',
  );
  const quantityControlRemoveStyles = clsx(
    'fd-quantity-control__remove-button',
    quantity === 0 && 'quantity--hidden',
    quantity > 0 && 'quantity--visible',
  );
  const quantityValueStyles = clsx(
    'fd-quantity-control__quantity-value',
    classes.quantityValue,
    quantityControlRemoveStyles,
  );

  return (
    <div className={quantityControlStyles}>
      {onRemoveItem && (
        <>
          <IconButton
            aria-label={`${translate('Remove')} ${itemName}`}
            className={quantityControlRemoveStyles}
            data-fd="quantity-control-remove"
            disabled={isRemoveItemDisabled}
            onClick={onRemoveItem}
          >
            <RemoveIcon size="s" />
          </IconButton>

          <Typography
            className={quantityValueStyles}
            data-fd="quantity-control-value"
            variant="body2"
          >
            {quantity}
          </Typography>
        </>
      )}

      <IconButton
        aria-label={`${translate('Add')} ${itemName}`}
        className={quantityControlAddStyles}
        data-fd="quantity-control-add"
        onClick={onAddItem}
      >
        <AddIcon size="s" />
      </IconButton>
    </div>
  );
};
